"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Chip,
  alpha,
  Alert,
  Fade,
  useTheme,
  InputAdornment,
  Grid,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import CategoryIcon from "@mui/icons-material/Category"
import RefreshIcon from "@mui/icons-material/Refresh"
import TablaTipoTransaccionComponent from "./TablaTipoTransaccionComponent.js"

const GestionTipoTransaccionComponent = () => {
  const theme = useTheme()
  const { tipoTransaccion, getTipoTransaccion, postTipoTransaccion ,isNavbarCollapsed,
  } = useContext(ErpContext)

  const [tipoTransaccionDescripcion, setTipoTransaccionDescripcion] = useState("")
  const [verEnCaja, setVerEnCaja] = useState(false)
  const [verEnTransaccion, setVerEnTransaccion] = useState(false)
  const [verEnColumna, setVerEnColumna] = useState("")
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchTipoTransaccion()
  }, [])

  const fetchTipoTransaccion = async () => {
    setLoading(true)
    try {
      await getTipoTransaccion()
    } catch (err) {
      console.error("Error al cargar tipos de transacción:", err)
      setError("No se pudieron cargar los tipos de transacción. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleTipoTransaccionSubmit = async (e) => {
    e.preventDefault()
    if (!tipoTransaccionDescripcion.trim()) {
      setError("Por favor ingrese una descripción para el tipo de transacción")
      return
    }

    setLoading(true)
    try {
      await postTipoTransaccion(
        tipoTransaccionDescripcion,
        verEnCaja,
        verEnTransaccion,
        verEnCaja ? verEnColumna : null
      )
      // Limpiar formulario
      setTipoTransaccionDescripcion("")
      setVerEnCaja(false)
      setVerEnTransaccion(false)
      setVerEnColumna("")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al agregar tipo de transacción:", err)
      setError("No se pudo agregar el tipo de transacción. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}>
      <Grid container spacing={2} sx={{ height: "100%" }}>
        {/* Encabezado y formulario */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: "visible",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ReceiptLongIcon
                  sx={{
                    fontSize: 28,
                    color: "rgb(244, 147, 14)",
                    mr: 1.5,
                  }}
                />
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                  Gestión de Tipos de Transacción
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <form
                onSubmit={handleTipoTransaccionSubmit}
                style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flexGrow: 1,
                  }}
                >
                  <TextField
                    label="Descripción del Tipo de Transacción"
                    variant="outlined"
                    fullWidth
                    value={tipoTransaccionDescripcion}
                    onChange={(e) => setTipoTransaccionDescripcion(e.target.value)}
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={verEnCaja}
                          onChange={(e) => {
                            setVerEnCaja(e.target.checked)
                            if (!e.target.checked) {
                              setVerEnColumna("")
                            }
                          }}
                          color="primary"
                          disabled={loading}
                        />
                      }
                      label="Ver en Caja"
                    />
                    {verEnCaja && (
                      <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                        <InputLabel id="ver-en-columna-label">Columna en Caja</InputLabel>
                        <Select
                          labelId="ver-en-columna-label"
                          value={verEnColumna}
                          onChange={(e) => setVerEnColumna(e.target.value)}
                          label="Columna en Caja"
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                          }}
                        >
                          <MenuItem value="Ingreso">Ingreso</MenuItem>
                          <MenuItem value="Egreso">Egreso</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={verEnTransaccion}
                          onChange={(e) => setVerEnTransaccion(e.target.checked)}
                          color="primary"
                          disabled={loading}
                        />
                      }
                      label="Ver en Transacción"
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={<AddCircleOutlineIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        boxShadow: 2,
                        minWidth: 140,
                        backgroundColor: "#5b5b5f",
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Agregar Tipo
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<RefreshIcon />}
                      onClick={fetchTipoTransaccion}
                      disabled={loading}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        minWidth: 100,
                      }}
                    >
                      Actualizar
                    </Button>
                  </Box>
                </Box>
              </form>

              {/* Alertas */}
              <Fade in={success}>
                <Alert
                  severity="success"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                  onClose={() => setSuccess(false)}
                >
                  Tipo de transacción agregado correctamente
                </Alert>
              </Fade>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de tipos de transacción */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2, justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CategoryIcon
                    sx={{
                      fontSize: 24,
                      color: "rgb(244, 147, 14)",
                      mr: 1.5,
                    }}
                  />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    Tipos de Transacción Disponibles
                  </Typography>
                  <Chip
                    label={`Total: ${tipoTransaccion.filter((tipo) => !tipo.eliminado).length}`}
                    size="small"
                    sx={{ ml: 2, bgcolor: alpha("#5b5b5f", 0.1), fontWeight: 500 }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={fetchTipoTransaccion}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    minWidth: 120,
                    borderColor: "rgb(244, 147, 14)",
                    color: "rgb(244, 147, 14)",
                    "&:hover": {
                      borderColor: alpha("rgb(244, 147, 14)", 0.8),
                      backgroundColor: alpha("rgb(244, 147, 14)", 0.05),
                    },
                  }}
                >
                  Actualizar
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <TablaTipoTransaccionComponent />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default GestionTipoTransaccionComponent
